/* eslint-disable react/prop-types */
/* eslint-disable linebreak-style */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Col, Form, Input, Modal, notification, Popover, Row, Tooltip, Typography
} from 'antd';
import { connect } from 'react-redux';
import { func, node } from 'prop-types';
import {
  CloseOutlined, EyeInvisibleOutlined, EyeOutlined, SettingOutlined, UserOutlined
} from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import { signOut } from '../redux/actions/authActions';
import history from '../services/history';
import { authenticateCurrentUser, changeProfilePassword } from '../services/auth';
import * as constants from '../utils/constants';
import { passwordPolicy } from '../utils/passwordPolicy';
import crcEvansLogo from '../assets/crc-evans-logo.png';
import crcEvansLogoArchive from '../assets/crc-evans-logo-archive.png';

const APP_VERSION = '0.1.0';

const PAGE_TITLES = {
  '/': 'Dashboard',
  '/SmartLync-HomePage': 'Dashboard',
  '/Admin': 'Admin',
  '/admin': 'Admin',
  '/generate-license': 'Generate License',
  '/SparePartsGenerator': 'Spare Parts Generator',
  '/SparePartsUpdate': 'Spare Parts Update',
  '/TrainingManagement': 'Training Management',
  '/TrackRecord': 'Track Record',
  '/Pricing': 'Pricing',
  '/Data360Pricing': 'Data360 Pricing',
  '/calibration': 'Calibration',
  '/welding-wire': 'Welding Wire',
  '/FinanceMapping': 'Finance Mapping',
  '/Project-margin': 'Project Margin',
  '/raw-data': 'Raw Data',
  '/general-data': 'General Data',
  '/weekly-reporting': 'Weekly Reporting',
  '/me-reporting': 'ME Reporting',
  '/checks3': 'Checks 3',
  '/check2-commercial': 'Check 2 Commercial',
  '/check1': 'Check 1',
  '/brazil': 'Brazil',
  '/supplement': 'Supplement',
  '/project-margin': 'Project Margin',
  '/Utilization': 'Utilization',
  '/EMEAA': 'EMEAA',
  '/CashFlow': 'Cash Flow',
};

const getPageTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  const match = Object.keys(PAGE_TITLES).find((key) => key !== '/' && pathname.startsWith(key));
  return match ? PAGE_TITLES[match] : 'SmartLync';
};

const AppShell = (props) => {
  const { children, onSignout, location } = props;
  const [profileVisible, setProfileVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [validatingStatus, setValidatingStatus] = useState('');
  const [validatingStatusConfirm, setValidatingStatusConfirm] = useState('');
  const [validateVisible, setValidateVisible] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loadBtn, setLoadBtn] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const [userGroup, setUserGroup] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const firstName = user?.['custom:first_name'] || '';
  const lastName = user?.['custom:last_name'] || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || user?.name || 'User';
  const initials = (`${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    || (fullName.slice(0, 2) || 'U').toUpperCase());
  const roleLabel = user?.['custom:role'] || (userGroup.includes('admin') ? 'Admin' : 'User');
  const pathname = location?.pathname || window.location.pathname;
  const pageTitle = useMemo(() => getPageTitle(pathname), [pathname]);
  // Pages own their titles/tabs (Admin-style) — hide duplicate shell header everywhere
  const hideShellHeader = true;
  const isDashboardActive = pathname === '/' || pathname === '/SmartLync-HomePage';
  const isAdminActive = pathname.toLowerCase().includes('admin');

  useEffect(() => {
    const element = document.getElementById('body-wrapper');
    sessionStorage.setItem('theme', 'True');
    localStorage.setItem('webTheme', 'light');
    if (element) element.classList.add('light-theme');
  }, []);

  useEffect(() => {
    authenticateCurrentUser()
      .then(async (data) => {
        if (data.signInUserSession.accessToken.payload['cognito:groups']) {
          setUserGroup(data.signInUserSession.accessToken.payload['cognito:groups']);
        }
      })
      .catch((error) => {
        console.log('error', error);
      });
  }, []);

  const validateToNextPassword = (e) => {
    const value = e.target.value;
    if (!value) {
      setValidateVisible(false);
      setValidatingStatus('');
    }
    if (value) {
      setNewPassword(value);
      setValidatingStatus('validating');
      setTimeout(() => {
        if (value.length > 7 && constants.PASSWORD_PATTERN.test(value)) {
          setValidatingStatus('success');
          if (confirmPassword.length > 7 && confirmPassword === value) {
            setValidatingStatusConfirm('success');
          } else {
            setValidatingStatusConfirm('warning');
          }
          setValidateVisible(false);
        } else {
          setValidatingStatus('warning');
          setValidateVisible(true);
        }
      }, 500);
    }
  };

  const validateToFirstPassword = (e) => {
    const value = e.target.value;
    if (value) {
      setValidatingStatusConfirm('validating');
      setConfirmPassword(value);
      setTimeout(() => {
        if (value.length > 7 && value === newPassword) {
          setValidatingStatusConfirm('success');
        } else {
          setValidatingStatusConfirm('warning');
        }
      }, 500);
    } else {
      setValidatingStatusConfirm('');
    }
  };

  const changeProfileCred = () => {
    changePasswordForm
      .validateFields()
      .then(async (data) => {
        if (validatingStatus === 'success') {
          if (validatingStatusConfirm === 'success') {
            await changeProfilePassword(data.currentPassword, data.newPassword);
            setLoadBtn(true);
            setTimeout(() => {
              setLoadBtn(false);
              changePasswordForm.resetFields();
              setChangePasswordVisible(false);
            }, 1000);
          } else {
            notification.warning({
              message: 'The New password and confirmation password do not match!'
            });
          }
        } else {
          notification.warning({
            message: 'Your password does not match Password policy!'
          });
        }
      });
  };

  const handleLogout = () => {
    onSignout();
    notification.destroy();
    localStorage.clear();
    sessionStorage.clear();
    notification.success({
      duration: 3,
      message: 'Successfully Logged Out',
      description: '',
    });
    history.push('/login');
  };

  return (
    <div className="crce-app-shell">
      {/* Sidebar — Data360 / CRC Evans pattern */}
      <aside className="crce-sidebar">
        <div className="crce-sidebar-brand">
          <img src={crcEvansLogo} alt="CRC Evans" className="crce-sidebar-brand-icon" />
          <div className="crce-sidebar-brand-text">
            <div className="crce-sidebar-brand-title">SmartLync</div>
            <div className="crce-sidebar-brand-sub">CRC Evans Platform</div>
          </div>
        </div>

        <nav className="crce-sidebar-nav">
          <button
            type="button"
            className={`crce-nav-item ${isDashboardActive ? 'is-active' : ''}`}
            onClick={() => history.push('/SmartLync-HomePage')}
          >
            <span className="crce-nav-icon">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
              </svg>
            </span>
            <span>Dashboard</span>
          </button>

          {userGroup && userGroup.includes('admin') && (
            <button
              type="button"
              className={`crce-nav-item ${isAdminActive ? 'is-active' : ''}`}
              onClick={() => history.push('/Admin')}
            >
              <span className="crce-nav-icon">
                <SettingOutlined style={{ fontSize: 16 }} />
              </span>
              <span>Admin</span>
            </button>
          )}
        </nav>

        <div className="crce-sidebar-footer">
          <button
            type="button"
            className="crce-user-chip"
            onClick={() => setProfileVisible(true)}
            title="Profile"
          >
            <div className="crce-user-avatar">{initials}</div>
            <div className="crce-user-meta">
              <div className="crce-user-name">{fullName}</div>
              <div className="crce-user-role">{roleLabel}</div>
            </div>
          </button>

          <button type="button" className="crce-logout-btn" onClick={handleLogout}>
            <span className="crce-nav-icon">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <path d="M16 13v-2H7V8l-5 4 5 4v-3h9zm3-10H5c-1.1 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
              </svg>
            </span>
            <span>Logout</span>
          </button>

          <div className="crce-powered">
            <div className="crce-powered-version">v{APP_VERSION}</div>
            <div className="crce-powered-label">Powered by</div>
            <img src={crcEvansLogoArchive} alt="CRC Evans" className="crce-powered-logo" />
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="crce-main">
        {!hideShellHeader && (
          <header className="crce-header">
            <div className="crce-header-inner">
              <div className="crce-header-titles">
                <h1 className="crce-page-title">{pageTitle}</h1>
              </div>
            </div>
          </header>
        )}

        <main className={`crce-content ${hideShellHeader ? 'crce-content--no-header' : ''}`}>
          <div className="crce-content-inner">
            {children}
          </div>
        </main>
      </div>

      <Modal
        width="45vw"
        className="pipeline-modal"
        title="Profile"
        centered
        visible={profileVisible}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setProfileVisible(false);
              setChangePasswordVisible(false);
              changePasswordForm.resetFields();
              setValidatingStatus('');
              setValidatingStatusConfirm('');
            }}
          >
            Close
          </Button>
        ]}
      >
        <div>
          <Row gutter={16}>
            <Col span={6}>
              <UserOutlined className="user_photo" />
            </Col>
            <Col span={18} style={{ display: 'grid' }}>
              <Typography.Text className="profile_text">{fullName}</Typography.Text>
              <Typography.Text className="profile_text">{user ? user.email : ''}</Typography.Text>
              <hr />
              <div>
                <Typography.Text
                  className="change_profile_password"
                  onClick={() => setChangePasswordVisible(true)}
                >
                  Change Password
                </Typography.Text>
                {changePasswordVisible && (
                  <Tooltip title="close" placement="right">
                    <CloseOutlined
                      className="close_password"
                      onClick={() => {
                        setChangePasswordVisible(false);
                        changePasswordForm.resetFields();
                        setValidateVisible(false);
                        setValidatingStatus('');
                        setValidatingStatusConfirm('');
                      }}
                    />
                  </Tooltip>
                )}
              </div>

              <Row className="profilePassowrd_form">
                {changePasswordVisible && (
                  <Col span={24}>
                    <Form
                      className="forgotPswdForm"
                      form={changePasswordForm}
                      name="normal_login"
                      initialValues={{ remember: true }}
                    >
                      <Form.Item
                        name="currentPassword"
                        rules={[{ required: true, message: 'Please input your current password!' }]}
                      >
                        <Input.Password
                          size="large"
                          type="password"
                          placeholder="Current Password"
                          iconRender={(visible) => (visible ? <EyeOutlined className="password_icon" /> : <EyeInvisibleOutlined className="password_icon" />)}
                        />
                      </Form.Item>
                      <Popover
                        placement="bottomRight"
                        title={constants.TITLE}
                        content={passwordPolicy}
                        trigger="focus"
                        visible={validateVisible}
                      >
                        <Form.Item
                          name="newPassword"
                          rules={[{ required: true, message: 'Please input your new password!' }]}
                          hasFeedback
                          validateStatus={validatingStatus}
                        >
                          <Input.Password
                            size="large"
                            type="password"
                            placeholder="New Password"
                            onChange={validateToNextPassword}
                            iconRender={(visible) => (visible ? <EyeOutlined className="password_icon" /> : <EyeInvisibleOutlined className="password_icon" />)}
                          />
                        </Form.Item>
                      </Popover>
                      <Form.Item
                        name="confirmPassword"
                        rules={[{ required: true, message: 'Please input your confirm password!' }]}
                        hasFeedback
                        validateStatus={validatingStatusConfirm}
                      >
                        <Input.Password
                          size="large"
                          type="password"
                          placeholder="Confirm New Password"
                          onChange={validateToFirstPassword}
                          iconRender={(visible) => (visible ? <EyeOutlined className="password_icon" /> : <EyeInvisibleOutlined className="password_icon" />)}
                        />
                      </Form.Item>
                      <Form.Item>
                        <Button
                          size="large"
                          block
                          htmlType="submit"
                          className="profile_password_button"
                          onClick={changeProfileCred}
                          loading={loadBtn}
                        >
                          Change Password
                        </Button>
                      </Form.Item>
                    </Form>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

AppShell.propTypes = {
  children: node,
  onSignout: func.isRequired,
};

AppShell.defaultProps = {
  children: null,
};

const mapDispatchToProps = {
  onSignout: signOut,
};

export default connect(null, mapDispatchToProps)(withRouter(AppShell));
